import { createDefaultDeliveryUpdateDto } from "@/lib/delivery-defaults";
import {
  DEFAULT_DELIVERY_TYPE_KEY,
  DEFAULT_FILE_TYPE_KEY,
  DEFAULT_COLLATION_TYPE_KEY,
  resolveGuid,
} from "@/lib/constants";
import { reportListItemApi } from "@/services/report-list-item-api";
import type { Parameter } from "@/types/parameter";
import type {
  ReportListItem,
  ReportListItemCreateRequest,
  ReportListItemUpdateMutationRequest,
} from "@/types/report-list";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./query-keys";
import { useAuthReady } from "./use-auth-ready";
import { useTenantIdNumber } from "./use-tenants";
import { buildParameterDictFromXml } from "./use-hydrated-report-list-items";

export function useReportListItems(reportListKey: string) {
  const isAuthReady = useAuthReady();

  return useQuery({
    queryKey: queryKeys.reportListItems.byList(reportListKey),
    queryFn: async () => {
      const items = await reportListItemApi.getReportListItemsByListKey(reportListKey);

      // Check for duplicate display orders and log warning
      if (hasDuplicateDisplayOrders(items)) {
        console.warn(
          `Duplicate displayOrder values detected in list ${reportListKey}. Consider using useNormalizeDisplayOrders to fix.`,
        );
      }

      // Always sort by displayOrder to ensure correct ordering
      return items.sort((a, b) => a.displayOrder - b.displayOrder);
    },
    enabled: isAuthReady && !!reportListKey,
  });
}

export function useReportListItem(reportListItemKey: string) {
  const isAuthReady = useAuthReady();

  return useQuery({
    queryKey: queryKeys.reportListItems.detail(reportListItemKey),
    queryFn: () => reportListItemApi.getReportListItemByKey(reportListItemKey),
    enabled: isAuthReady && !!reportListItemKey,
  });
}

export function useCreateReportListItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ReportListItemCreateRequest) =>
      reportListItemApi.createReportListItem(request),
    onSuccess: (_data, variables) => {
      // Targeted invalidation: only the specific list's items
      queryClient.invalidateQueries({
        queryKey: queryKeys.reportListItems.byList(
          variables.reportListItem.reportListKey,
        ),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.all });
    },
  });
}

export function useCreateMultipleReportListItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requests: ReportListItemCreateRequest[]) =>
      reportListItemApi.createMultipleReportListItems(requests),
    onSuccess: (_data, variables) => {
      // Targeted invalidation: only the affected list's items
      const listKey = variables[0]?.reportListItem.reportListKey;
      if (listKey) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.reportListItems.byList(listKey),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: queryKeys.reportListItems.all,
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.all });
    },
  });
}

export function useUpdateReportListItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ReportListItemUpdateMutationRequest) =>
      reportListItemApi.updateReportListItem(request),
    onMutate: async (request) => {
      const listKey = request.reportListItem.reportListKey;
      const itemKey = request.reportListItem.reportListItemKey;

      await queryClient.cancelQueries({
        queryKey: queryKeys.reportListItems.byList(listKey),
      });

      const previousItems = queryClient.getQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(listKey),
      );

      // Optimistically update the item in the list cache
      queryClient.setQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(listKey),
        (old) =>
          old?.map((item) =>
            item.reportListItemKey === itemKey
              ? {
                  ...item,
                  displayName: request.reportListItem.displayName,
                  displayOrder: request.reportListItem.displayOrder,
                  reportListItemParameters:
                    request.reportListItem.reportListItemParameters,
                  // Optimistically set report.parameters so badges update
                  // before the hydration query resolves
                  ...(request._optimisticParameters && item.report
                    ? {
                        report: {
                          ...item.report,
                          parameters: request._optimisticParameters,
                        },
                      }
                    : {}),
                }
              : item,
          ),
      );

      // Seed the parameter query cache so useHydratedReportListItems
      // reads from cache instead of firing a new API fetch.
      if (request._optimisticParameters && request._reportPath) {
        const newParamValues = buildParameterDictFromXml(
          request.reportListItem.reportListItemParameters,
        );
        queryClient.setQueryData<Parameter[]>(
          queryKeys.reports.parameters(request._reportPath, newParamValues),
          request._optimisticParameters,
        );
      }

      return { previousItems, listKey };
    },
    onError: (_err, _request, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(
          queryKeys.reportListItems.byList(context.listKey),
          context.previousItems,
        );
      }
    },
    onSettled: (_data, _err, variables) => {
      // Targeted invalidation: only the specific list's items
      queryClient.invalidateQueries({
        queryKey: queryKeys.reportListItems.byList(
          variables.reportListItem.reportListKey,
        ),
      });
      // Invalidate parameter queries so hydration refetches with updated XML
      queryClient.invalidateQueries({
        queryKey: queryKeys.reports.allParameters(),
      });
    },
  });
}

export function useDeleteReportListItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      reportListItemKey,
    }: {
      reportListItemKey: string;
      reportListKey: string;
    }) => reportListItemApi.deleteReportListItem(reportListItemKey),
    onMutate: async ({ reportListItemKey, reportListKey }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.reportListItems.byList(reportListKey),
      });

      const previousItems = queryClient.getQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(reportListKey),
      );

      // Optimistically remove the item from cache
      queryClient.setQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(reportListKey),
        (old) =>
          old?.filter((item) => item.reportListItemKey !== reportListItemKey),
      );

      return { previousItems, reportListKey };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(
          queryKeys.reportListItems.byList(context.reportListKey),
          context.previousItems,
        );
      }
    },
    onSettled: (_data, _err, variables) => {
      // Targeted invalidation: only the affected list
      queryClient.invalidateQueries({
        queryKey: queryKeys.reportListItems.byList(variables.reportListKey),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.all });
    },
  });
}

export function useDeleteMultipleReportListItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ keys }: { keys: string[]; reportListKey: string }) =>
      reportListItemApi.deleteMultipleReportListItems(keys),
    onMutate: async ({ keys, reportListKey }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.reportListItems.byList(reportListKey),
      });

      const previousItems = queryClient.getQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(reportListKey),
      );

      const keysSet = new Set(keys);

      // Optimistically remove all items from cache
      queryClient.setQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(reportListKey),
        (old) => old?.filter((item) => !keysSet.has(item.reportListItemKey)),
      );

      return { previousItems, reportListKey };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(
          queryKeys.reportListItems.byList(context.reportListKey),
          context.previousItems,
        );
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.reportListItems.byList(variables.reportListKey),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.all });
    },
  });
}

export function useReorderReportListItems() {
  const tenantId = useTenantIdNumber();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      changedItems,
    }: {
      items: ReportListItem[];
      changedItems: ReportListItem[];
    }) => {
      // Only update items whose displayOrder actually changed
      if (changedItems.length === 0) return;

      await Promise.all(
        changedItems.map((item) =>
          reportListItemApi.updateReportListItem({
            reportListItem: {
              reportListItemKey: item.reportListItemKey,
              reportListKey: item.reportListKey,
              reportKey: item.reportKey,
              reportDeliveryKey: item.reportDeliveryKey,
              displayName: item.displayName,
              displayOrder: item.displayOrder,
              reportListItemParameters: item.reportListItemParameters,
              lastModifiedUserKey: item.lastModifiedUserKey || "system",
              tenantId,
            },
            reportDelivery: createDefaultDeliveryUpdateDto({
              reportDeliveryKey: item.reportDeliveryKey,
              reportDeliveryTypeKey: resolveGuid(
                item.reportDelivery?.reportDeliveryTypeKey,
                DEFAULT_DELIVERY_TYPE_KEY,
              ),
              reportFileTypeKey: resolveGuid(
                item.reportDelivery?.reportFileTypeKey,
                DEFAULT_FILE_TYPE_KEY,
              ),
              reportCollationTypeKey: resolveGuid(
                item.reportDelivery?.reportCollationTypeKey,
                DEFAULT_COLLATION_TYPE_KEY,
              ),
              createdUserKey: item.createdUserKey || "system",
              lastModifiedUserKey: item.lastModifiedUserKey || "system",
              tenantId,
            }),
          }),
        ),
      );
    },
    onMutate: async ({ items }) => {
      const listKey = items[0]?.reportListKey;
      if (!listKey) return { previousItems: undefined, listKey: "" };

      await queryClient.cancelQueries({
        queryKey: queryKeys.reportListItems.byList(listKey),
      });

      const previousItems = queryClient.getQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(listKey),
      );

      // Optimistically apply the new order
      queryClient.setQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(listKey),
        items,
      );

      return { previousItems, listKey };
    },
    onError: (_err, _items, context) => {
      if (context?.previousItems && context.listKey) {
        queryClient.setQueryData(
          queryKeys.reportListItems.byList(context.listKey),
          context.previousItems,
        );
      }
    },
  });
}

/**
 * Checks if a list of items has any duplicate displayOrder values
 */
export function hasDuplicateDisplayOrders(items: ReportListItem[]): boolean {
  const displayOrders = items.map((item) => item.displayOrder);
  const uniqueOrders = new Set(displayOrders);
  return displayOrders.length !== uniqueOrders.size;
}

/**
 * Normalizes display orders to sequential values (0, 1, 2, 3...)
 * Returns an array of items with corrected displayOrder values
 */
export function normalizeDisplayOrders(
  items: ReportListItem[],
): ReportListItem[] {
  // Sort by current display order first
  const sorted = [...items].sort((a, b) => a.displayOrder - b.displayOrder);

  // Reassign sequential display orders
  return sorted.map((item, index) => ({
    ...item,
    displayOrder: index,
  }));
}

/**
 * Hook to normalize display orders for a report list
 * Detects duplicates and reassigns sequential values if needed
 */
export function useNormalizeDisplayOrders() {
  const tenantId = useTenantIdNumber();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      items
    }: {
      items: ReportListItem[];
      listKey: string;
    }) => {
      // Normalize to sequential order
      const normalizedItems = normalizeDisplayOrders(items);

      // Find items that actually changed
      const originalOrderByKey = new Map(
        items.map((i) => [i.reportListItemKey, i.displayOrder] as const),
      )

      const changedItems = normalizedItems.filter(
        (normalizedItem) =>
          originalOrderByKey.get(normalizedItem.reportListItemKey) !==
          normalizedItem.displayOrder,
      )

      if (changedItems.length === 0) return;

      // Update changed items in the database
      await Promise.all(
        changedItems.map((item) =>
          reportListItemApi.updateReportListItem({
            reportListItem: {
              reportListItemKey: item.reportListItemKey,
              reportListKey: item.reportListKey,
              reportKey: item.reportKey,
              reportDeliveryKey: item.reportDeliveryKey,
              displayName: item.displayName,
              displayOrder: item.displayOrder,
              reportListItemParameters: item.reportListItemParameters,
              lastModifiedUserKey: item.lastModifiedUserKey || "system",
              tenantId,
            },
            reportDelivery: createDefaultDeliveryUpdateDto({
              reportDeliveryKey: item.reportDeliveryKey,
              reportDeliveryTypeKey:
                item.reportDelivery?.reportDeliveryTypeKey || "",
              reportFileTypeKey: item.reportDelivery?.reportFileTypeKey || "",
              reportCollationTypeKey:
                item.reportDelivery?.reportCollationTypeKey || "",
              createdUserKey: item.createdUserKey || "system",
              lastModifiedUserKey: item.lastModifiedUserKey || "system",
              tenantId,
            }),
          }),
        ),
      );

      return { normalizedItems, changedCount: changedItems.length };
    },
    onMutate: async ({ items, listKey }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.reportListItems.byList(listKey),
      });

      const previousItems = queryClient.getQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(listKey),
      );

      // Optimistically apply normalized order
      const normalizedItems = normalizeDisplayOrders(items);
      queryClient.setQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(listKey),
        normalizedItems,
      );

      return { previousItems, listKey };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousItems && context.listKey) {
        queryClient.setQueryData(
          queryKeys.reportListItems.byList(context.listKey),
          context.previousItems,
        );
      }
    },
  });
}
